/**
 * Authenticated fetch helper for org-scoped API calls.
 *
 * Automatically injects Bearer token, retries once on 401 with a token
 * refresh, and navigates to /login if the session refresh cannot be recovered.
 * If a refreshed request still gets a 401, the response is returned to the
 * caller: one endpoint-level authorization failure should not erase the
 * whole org console session. The exception is an identity-level 401 (the
 * backend rotated the user's oauthSubject — password reset, suspension,
 * sign-out-all): that session can never recover, so it is cleared and the
 * user is sent to /login.
 * Callers can opt out of the redirect via `redirectOnUnauthorized: false`;
 * the refresh-and-retry still runs (long-lived sessions must be able to
 * rotate an expired access token from any call site), but an unrecoverable
 * 401 is then returned as a normal API failure without clearing the org
 * session or navigating.
 */

import { useOrgAuthStore } from "../stores/useOrgAuthStore";
import { CLIENT_OPERATING_CONTEXT_HEADER } from "../types/operatingContext";

import type { OAuthConfig } from "./orgAuthService";
import { ensureValidToken } from "./orgAuthService";

/**
 * Opt-in fetch interceptor for dev/sim mocking. Default `null` → `orgAuthFetch`
 * behaves exactly as before and **live calls are untouched**. When an app
 * registers an interceptor (e.g. a `?sim=1` mock layer), it runs first:
 * returning a `Response` short-circuits the real network + auth chain (mocking
 * the return); returning `null` falls through to the normal live request.
 * Registered per-app at startup via `setOrgAuthFetchInterceptor`.
 */
export type OrgAuthFetchInterceptor = (
    input: string | URL,
    init: RequestInit | undefined,
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">
) => Promise<Response | null>;

export interface OrgAuthFetchOptions {
    redirectOnUnauthorized?: boolean;
}

let interceptor: OrgAuthFetchInterceptor | null = null;

export function setOrgAuthFetchInterceptor(fn: OrgAuthFetchInterceptor | null): void {
    interceptor = fn;
}

/**
 * The backend's `resolveOrgUser` guard returns this exact message when the
 * token's subject no longer matches any active OrgUser — i.e. the server
 * rotated the user's `oauthSubject` (password reset, suspension, sign-out-all)
 * and every token from before the rotation is permanently dead, even though
 * the OAuth refresh grant may still succeed.
 */
const STALE_IDENTITY_ERROR = "Org user not found or inactive";

async function isStaleIdentityResponse(response: Response): Promise<boolean> {
    try {
        const body = (await response.clone().json()) as {
            error?: string;
        } | null;
        return body?.error === STALE_IDENTITY_ERROR;
    } catch {
        return false;
    }
}

function buildOrgAuthHeaders(
    headers: RequestInit["headers"] | undefined,
    accessToken: string,
    selectedClientContextUuid: string | null
): Headers {
    const nextHeaders = new Headers(headers);
    nextHeaders.set("Authorization", `Bearer ${accessToken}`);

    if (selectedClientContextUuid && !nextHeaders.has(CLIENT_OPERATING_CONTEXT_HEADER)) {
        nextHeaders.set(CLIENT_OPERATING_CONTEXT_HEADER, selectedClientContextUuid);
    }

    return nextHeaders;
}

export async function orgAuthFetch(
    input: string | URL,
    init: RequestInit | undefined,
    config: Pick<OAuthConfig, "apiBase" | "clientId" | "redirectUri">,
    options: OrgAuthFetchOptions = {}
): Promise<Response> {
    // Sim/mock short-circuit — only when an app has opted in. A returned
    // Response means "mock the return"; null means proceed with the live call.
    if (interceptor) {
        const mocked = await interceptor(input, init, config);
        if (mocked) return mocked;
    }

    const store = useOrgAuthStore.getState();
    const redirectOnUnauthorized = options.redirectOnUnauthorized ?? true;

    if (!store.tokens) {
        if (!redirectOnUnauthorized) {
            return new Response(JSON.stringify({ error: "Unauthorized" }), {
                status: 401,
                headers: { "Content-Type": "application/json" }
            });
        }
        window.location.replace("/login");
        // Return a never-resolving promise since we're navigating away
        return new Promise(() => undefined);
    }

    const makeRequest = (accessToken: string): Promise<Response> =>
        fetch(input, {
            ...init,
            headers: buildOrgAuthHeaders(
                init?.headers,
                accessToken,
                useOrgAuthStore.getState().selectedClientContextUuid
            )
        });

    let response = await makeRequest(store.tokens.accessToken);

    if (response.status !== 401) {
        return response;
    }

    // Attempt token refresh — ensureValidToken deduplicates concurrent
    // requests. This runs regardless of `redirectOnUnauthorized`: opting out
    // of the redirect must not also opt out of refresh, or long-lived
    // sessions lose every workflow behind an opted-out call site as soon as
    // the access token expires.
    try {
        const result = await ensureValidToken(
            { ...config, scope: store.tokens.scope },
            store.tokens,
            // Force a refresh: a 401 means the current access token is unusable
            // now (revoked / rotated / scope-changed server-side), regardless of
            // its clock-based expiry. POSITIVE_INFINITY makes the skew check
            // never short-circuit; ensureValidToken still dedups concurrent
            // refreshes via its internal refreshPromise.
            Number.POSITIVE_INFINITY
        );
        const refreshedTokens = result.refreshed ? result.tokens : store.tokens;
        useOrgAuthStore.getState().setTokens(refreshedTokens);
        response = await makeRequest(refreshedTokens.accessToken);
    } catch {
        if (!redirectOnUnauthorized) {
            // Refresh failed but the caller opted out of session teardown —
            // surface the original 401 as a normal API failure.
            return response;
        }
        // Refresh failed — clear auth and redirect to login
        useOrgAuthStore.getState().clearAuth();
        window.location.replace("/login");
        return new Promise(() => undefined);
    }

    if (response.status === 401) {
        // Still 401 after a successful refresh. An identity-level rejection
        // means the subject was rotated server-side and no token this session
        // can ever mint will be accepted — clear the session instead of
        // leaving a signed-in-looking console where every request fails.
        if (redirectOnUnauthorized && (await isStaleIdentityResponse(response))) {
            useOrgAuthStore.getState().clearAuth();
            window.location.replace("/login");
            return new Promise(() => undefined);
        }
        // Anything else is an endpoint-level failure: the refresh succeeded,
        // so preserve the session and let the caller handle it.
        return response;
    }

    return response;
}
